import { useMemo } from 'react';
import { $api } from 'src/services/api';
import { IconName } from 'src/components/ui/Icon/Icon';
import { SearchFieldsRegistry, SearchModels } from 'src/components/ui/TableOverview/utils/SearchFieldRegistry';
import { buildSearchDomain } from 'src/components/ui/TableOverview/utils/buildSearchDomain';
import { useRouteAccess } from 'src/hooks/useRouteAccess';

const RESULTS_PER_CATEGORY = 6;

// Mirrors DataView.isIntegerField — these fields are matched with `=` against a
// numeric search term instead of `ilike`.
const isIntegerField = (field: string) => field === 'id' || field === 'partner_id.zip';

export type SearchHit = {
  model: string;
  id: number;
  label: string;
  subtitle?: string;
  to: string;
};

type BaseCategory = {
  key: string; // i18n key under commandPalette.categories.*
  endpoint: string; // generated OpenAPI path
  icon: IconName;
  /**
   * Route used to gate this category by the user's access rights, so entities
   * behind an inactive feature flag are never queried or surfaced. By default
   * matched exactly against `/${accessLink}` (as in routes.ts); set
   * `accessMatch: 'includes'` for access rights that are not nav routes (e.g. the
   * Settings `manage_users` sub-tab). See useRouteAccess.
   */
  accessLink: string;
  accessMatch?: 'exact' | 'includes';
  /** Detail-view (or fallback list) URL for a record id. */
  to: (id: number) => string;
  /** Map one raw record to a display hit. */
  map: (row: any) => { label: string; subtitle?: string };
};

/**
 * `domain` categories use the modern paginated endpoints (server-side `domain`
 * search → `{ items }`). `client` categories hit legacy endpoints that return a
 * plain array with no search params, so the (small) list is fetched once and
 * filtered in-memory against `searchText`.
 */
export type SearchCategory =
  | (BaseCategory & { mode: 'domain'; model: SearchModels })
  | (BaseCategory & { mode: 'client'; searchText: (row: any) => string });

// The categories the palette fans out over. Order = display order.
// NOTE: purchase.order (Inbounds) has no working detail overlay yet —
// /warehouse/inbound/:id collides with the inbound-create wizard — so its hits
// navigate to the inbound list instead of a record overlay (see Layout.tsx).
// Several other entities (mappings, carriers, countries) likewise have no record
// detail route and fall back to their list page.
export const SEARCH_CATEGORIES: SearchCategory[] = [
  {
    mode: 'domain',
    key: 'orders',
    model: SearchModels.SaleOrder,
    endpoint: '/sale_orders',
    icon: 'local-shipping',
    accessLink: 'outbound',
    to: id => `/outbound/${id}`,
    map: row => ({ label: row.name, subtitle: row.partner?.name ?? row.partner_id?.name }),
  },
  {
    mode: 'domain',
    key: 'contacts',
    model: SearchModels.Partner,
    endpoint: '/partners',
    icon: 'account',
    accessLink: 'contact',
    to: id => `/contact/${id}`,
    map: row => ({ label: row.name, subtitle: row.company_name || row.email }),
  },
  {
    mode: 'domain',
    key: 'products',
    model: SearchModels.Product,
    endpoint: '/products',
    icon: 'package',
    accessLink: 'warehouse/inventory',
    to: id => `/warehouse/inventory/${id}`,
    map: row => ({ label: row.name, subtitle: row.default_code || row.barcode }),
  },
  {
    mode: 'domain',
    key: 'cases',
    model: SearchModels.Case,
    endpoint: '/cases/case',
    icon: 'note',
    accessLink: 'cases',
    to: id => `/cases/${id}`,
    map: row => ({ label: row.topic ?? row.name, subtitle: row.assigned_user }),
  },
  {
    mode: 'domain',
    key: 'claims',
    model: SearchModels.Claim,
    endpoint: '/claims/claim',
    icon: 'note',
    accessLink: 'cases/claims',
    to: id => `/cases/claims/${id}`,
    map: row => ({ label: row.name, subtitle: row.order_number || row.tracking_number || row.carrier }),
  },
  {
    mode: 'domain',
    key: 'returns',
    model: SearchModels.Return,
    endpoint: '/return_pickings',
    icon: 'returns',
    accessLink: 'returns',
    to: id => `/returns/${id}`,
    map: row => ({ label: row.name, subtitle: row.partner?.name ?? row.partner_id?.name }),
  },
  {
    mode: 'domain',
    key: 'inbounds',
    model: SearchModels.Inbound,
    endpoint: '/stock/inbound',
    icon: 'inbound',
    accessLink: 'warehouse/inbound',
    to: () => `/warehouse/inbound`,
    map: row => ({ label: row.name, subtitle: row.partner?.name ?? row.partner_id?.name }),
  },
  {
    mode: 'client',
    key: 'forecasts',
    endpoint: '/customer-forecasts',
    icon: 'bar-chart',
    accessLink: 'forecasts',
    to: id => `/forecasts/${id}`,
    searchText: row => `${row.year} ${row.month} ${row.year}-${String(row.month).padStart(2, '0')} ${row.total_pakete}`,
    map: row => ({
      label: `${row.year}-${String(row.month).padStart(2, '0')}`,
      subtitle: row.total_pakete != null ? `${row.total_pakete}` : undefined,
    }),
  },
  {
    mode: 'client',
    key: 'promotions',
    endpoint: '/customer-forecast-promotions',
    icon: 'bar-chart',
    accessLink: 'forecasts',
    // No per-record detail route — open the promotions tab on the forecasts page.
    to: () => `/forecasts?tab=promotions`,
    searchText: row => `${row.name} ${row.type}`,
    map: row => ({ label: row.name, subtitle: row.type }),
  },
  {
    mode: 'domain',
    key: 'workflows',
    model: SearchModels.Workflow,
    endpoint: '/workflows',
    icon: 'account-tree',
    accessLink: 'workflows',
    to: id => `/workflows/${id}`,
    map: row => ({ label: row.name, subtitle: row.description || row.model }),
  },
  {
    mode: 'domain',
    key: 'carriers',
    model: SearchModels.Carrier,
    endpoint: '/carrier',
    icon: 'package',
    accessLink: 'carrier-analytics/carrier-list',
    to: () => `/carrier-analytics/carrier-list`,
    map: row => ({ label: row.name, subtitle: row.description }),
  },
  {
    mode: 'domain',
    key: 'countries',
    model: SearchModels.Country,
    endpoint: '/country/country',
    icon: 'public',
    accessLink: 'carrier-analytics/countries-and-groups',
    to: () => `/carrier-analytics/countries-and-groups`,
    map: row => ({ label: row.name, subtitle: row.continent_name }),
  },
  {
    mode: 'domain',
    key: 'country_groups',
    model: SearchModels.CountryGroup,
    endpoint: '/country/groups',
    icon: 'public',
    accessLink: 'carrier-analytics/countries-and-groups',
    to: id => `/carrier-analytics/countries-and-groups/edit/${id}`,
    map: row => ({ label: row.name, subtitle: row.description }),
  },
  {
    // Available channels (Billbee, Shopify, …) — a small static array endpoint,
    // so client-filtered. No per-template detail route; open the connectors page.
    mode: 'client',
    key: 'integrations',
    endpoint: '/shop_connectors/templates/',
    icon: 'integrations',
    accessLink: 'integrations/connectors',
    to: () => `/integrations/connectors`,
    searchText: row => `${row.channel?.name ?? ''} ${row.channel?.description ?? ''}`,
    map: row => ({ label: row.channel?.name ?? `#${row.id}`, subtitle: row.channel?.description }),
  },
  {
    // The user's connected shop instances (named by the user).
    mode: 'domain',
    key: 'connectors',
    model: SearchModels.Connector,
    endpoint: '/shop_connectors/',
    icon: 'integrations',
    accessLink: 'integrations/connectors',
    to: id => `/integrations/connectors/${id}`,
    map: row => ({ label: row.name, subtitle: row.template?.channel?.name }),
  },
  {
    mode: 'domain',
    key: 'mappings',
    model: SearchModels.Mappings,
    endpoint: '/shop_connectors/mappings/product',
    icon: 'integrations',
    accessLink: 'integrations/mappings',
    to: () => `/integrations/mappings`,
    map: row => ({ label: row.name || `#${row.id}`, subtitle: row.sku || row.barcode || row.connector_name }),
  },
  {
    mode: 'domain',
    key: 'users',
    model: SearchModels.User,
    endpoint: '/users/user/',
    icon: 'account',
    accessLink: 'manage_users',
    accessMatch: 'includes',
    to: id => `/settings/edit/${id}`,
    map: row => ({ label: row.name, subtitle: row.email }),
  },
];

function useCategoryHits(category: SearchCategory, search: string, enabled: boolean) {
  const isDomain = category.mode === 'domain';
  const fields = isDomain ? SearchFieldsRegistry[(category as any).model as SearchModels] ?? [] : [];

  // Domain mode searches server-side, so the query param carries the term;
  // client mode fetches the whole (small) list once and filters in-memory.
  const domain = useMemo(() => {
    if (!isDomain) return '[]';
    const built = buildSearchDomain(search, fields, isIntegerField);
    return JSON.stringify(built ?? []);
    // fields/isDomain are stable per category
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const query = $api.useQuery(
    'get',
    category.endpoint as any,
    isDomain ? { params: { query: { page: 1, size: RESULTS_PER_CATEGORY, domain } } } : {},
    { enabled },
  );

  const hits: SearchHit[] = useMemo(() => {
    const toHit = (row: any): SearchHit => {
      const mapped = category.map(row);
      return {
        model: category.mode === 'domain' ? category.model : category.key,
        id: row.id,
        label: mapped.label,
        subtitle: mapped.subtitle,
        to: category.to(row.id),
      };
    };

    if (isDomain) {
      const items: any[] = (query.data as any)?.items ?? [];
      return items.map(toHit);
    }

    // Client mode: legacy array endpoint, filter against searchText.
    const rows: any[] = Array.isArray(query.data) ? (query.data as any[]) : (query.data as any)?.items ?? [];
    const q = search.trim().toLowerCase();
    return rows
      .filter(row => (category as any).searchText(row).toLowerCase().includes(q))
      .slice(0, RESULTS_PER_CATEGORY)
      .map(toHit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, search]);

  return { hits, isFetching: query.isFetching };
}

export type CategoryResult = { category: SearchCategory; hits: SearchHit[] };

/**
 * Fans out the search term across all SEARCH_CATEGORIES in parallel. Queries are
 * disabled until the palette is open, a (debounced) term is present, and the
 * user actually has access to the category's route — so entities behind an
 * inactive feature flag are never queried or surfaced.
 */
export function useGlobalSearch(search: string, open: boolean) {
  const enabled = open && search.trim().length > 0;
  const { hasAccess, hasAccessIncludes } = useRouteAccess();

  // Fixed-length list → fixed hook order (rules of hooks are satisfied).
  const results = SEARCH_CATEGORIES.map(category => {
    const canAccess =
      category.accessMatch === 'includes' ? hasAccessIncludes(category.accessLink) : hasAccess(category.accessLink);
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { hits, isFetching } = useCategoryHits(category, search.trim(), enabled && canAccess);
    return { category, hits, isFetching };
  });

  const isFetching = results.some(r => r.isFetching);
  const categories: CategoryResult[] = results
    .filter(r => r.hits.length > 0)
    .map(r => ({ category: r.category, hits: r.hits }));
  const flatHits = categories.flatMap(c => c.hits);

  return { enabled, isFetching, categories, flatHits };
}
